import React, { useState } from 'react';
import { AutoComplete } from '@progress/kendo-react-dropdowns';
import useDebounce from '../../hooks/useDebounce';
import styled from 'styled-components';
import tw from 'twin.macro';
import cn from 'classnames';
import { nanoid } from 'nanoid';
import useTheme from '@material-ui/core/styles/useTheme';

interface Props {
  id?: string;
  label?: string;
  className?: string;
  error?: boolean;
  fullWidth?: boolean;
  required?: boolean;
  helperText?: string;
  value: any;
  onChange: (value: string) => void;
}

export const AffiliationInputText: React.FC<Props> = (props) => {
  const { id, className, error, fullWidth, required, label, helperText } = props;

  const [options, setOptions] = useState<string[]>([]);
  const debounce = useDebounce();
  const inputId = id || nanoid();
  const theme = useTheme();

  const parseOptions = (data: { items: [] }): string[] => {
    const opts: string[] = [];
    data.items.forEach((item: any) => {
      item.names
        .filter((name: any) => name.types.includes('label') && name.types.includes('ror_display'))
        .forEach((name: any) => {
          opts.push(name.value);
        });
    });

    return opts;
  };

  async function fetchOptions(value: string) {
    const url = `https://api.ror.org/v2/organizations?query=${value}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        setOptions([]);
      }

      const result = await response.json();
      setOptions(parseOptions(result));
    } catch (error) {
      setOptions([]);
    }
  }

  const onChange = async (e: any) => {
    setOptions([]);
    const value = e.target.value;
    props.onChange(value);
    debounce(() => {
      if (value) fetchOptions(value);
    }, 400);
  };

  return (
    <StyledDiv className={cn(className, { error, fullWidth })}>
      {label && (
        <label htmlFor={inputId} className={`${error ? 'text-red-600' : theme.palette.text.primary}`}>
          {required ? `${label} *` : label}
        </label>
      )}
      <AutoComplete
        style={{
          width: '100%',
        }}
        clearButton={false}
        data={options}
        value={props.value}
        onChange={onChange}
      />
      <span className={cn('helperText pt-1.5', { error })}>{helperText}</span>
    </StyledDiv>
  );
};

const StyledDiv = styled.div`
  label {
    ${tw`mb-1.5 text-sm`}
    font-weight: normal;
    display: inline-block;
  }

  .k-searchbar {
    input {
      height: 2.4375rem;
      border-radius: 0.25rem;
      background-color: #fff;
      border: 1px solid #afb0b1;
    }

    input:hover {
      border: 1px solid #000000;
    }

    input:focus-within {
      border: ${(props) => '2px solid' + props.theme.palette.primary.main};
    }
  }

  .k-autocomplete:focus-within {
    box-shadow: none;
  }

  .helperText {
    ${tw`text-sm m-0.5 mb-1 leading-4 h-4`}
    color:  ${(props) => props.theme.palette.text.secondary};
    display: none;

    &.error {
      color: ${(props) => props.theme.palette.error.main};
      display: block;
    }
  }

  &.error {
    label {
      color: ${(props) => props.theme.palette.error.main};
    }

    .k-searchbar {
      input {
        border: ${(props) => '2px solid' + props.theme.palette.error.main};
      }

      input:hover {
        border: ${(props) => '2px solid' + props.theme.palette.error.main};
      }

      input:focus-within {
        border: ${(props) => '2px solid' + props.theme.palette.error.main};
      }
    }
  }
`;
